/**
 * What: One bar for this month's credits, always shown when a balance exists. Full width is the monthly allowance,
 *   the green fill from the left is what is available now, and the grey track is what was already used. Once a
 *   cost estimate exists, the darker striped end of the green is what this test would use and a soft label says
 *   so. When the test needs more than is available, the striped segment turns critical and the label reads
 *   "Short by N". The exact numbers are in the hover text.
 * Transport / data-flow: Pure presentation over three numbers; the caller owns where they come from.
 *
 * The bar is hand-built from plain `<div>`s (no `s-progress-bar` in @shopify/polaris-types yet), with no
 * fill animation so scaling never squashes the labels. The web-component React bindings don't pass through
 * `role`/`aria-*`, so the `progressbar` role and its value semantics live on a plain wrapping `div`.
 */
import type { StudyBuilderCreditBar } from '@testfeed/shopify-contracts'
import './CreditUsageBar.css'

/** Geometry only: the server supplies shortage meaning, wording and accessible values. */
function percentOf(part: number, whole: number) {
  return whole > 0 ? Math.min(100, (Math.max(0, part) / whole) * 100) : 0
}

export function CreditUsageBar({ bar }: { bar: StudyBuilderCreditBar }) {
  const availablePercent = percentOf(bar.available, bar.maximum)
  const testPercentOfAvailable = percentOf(bar.estimate ?? 0, bar.available)
  return (
    <div
      role="progressbar"
      aria-valuenow={bar.available}
      aria-valuemin={0}
      aria-valuemax={bar.maximum}
      aria-label={bar.description}
      title={bar.description}
      className="tf-credit-bar"
      data-short={bar.tone === 'critical'}
    >
      <div className="tf-credit-bar__available" style={{ inlineSize: `${availablePercent}%` }}>
        {bar.estimate === null ? null : (
          <div className="tf-credit-bar__test" style={{ inlineSize: `${testPercentOfAvailable}%` }} />
        )}
      </div>
      <div className="tf-credit-bar__labels">
        <s-text>{bar.availableLabel}</s-text>
        {bar.estimateLabel ? (
          <s-text tone={bar.tone === 'critical' ? 'critical' : undefined}>{bar.estimateLabel}</s-text>
        ) : null}
      </div>
    </div>
  )
}
